import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
} from "@/components/ui/select";
import WorkspaceModal from "@/components/WorkspaceModal";
import { useActiveOrgWorkspaces } from "@/hooks/organizations";
import useActiveWorkspace from "@/hooks/useActiveWorkspace";
import { useCurrentUser } from "@/queries/users";
import {
  useDeleteWorkspaceInvite,
  useWorkspaceInvites,
} from "@/queries/workspace-invites";
import { useCreateWorkspaceMember } from "@/queries/workspace-members";
import { WorkspaceInvite } from "@/types/workspace-invite";

import { CheckCircle2, FolderOpen, PlusSquare, XCircle } from "lucide-react";
import { useState } from "react";

import LoadingSpinner from "@/components/LoadingSpinner";
import { Button } from "@/components/ui/button";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { BriefcaseIcon } from "@heroicons/react/outline";
import { useNavigate } from "react-router-dom";
import SelectItemIcon from "./SelectItemIcon";

const WorkspaceInvites = () => {
  const currentUser = useCurrentUser();
  const invitesQuery = useWorkspaceInvites({
    invitee_email: currentUser.data?.email,
    status: "pending",
  });
  const workspaceInvites = invitesQuery.data?.items || [];
  if (invitesQuery.isLoading || currentUser.isLoading)
    return (
      <div className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50 focus:bg-gray-50">
        <LoadingSpinner size={4} />
        <span>Loading invites...</span>
      </div>
    );
  if (workspaceInvites.length === 0) return null;

  return (
    <SelectGroup>
      <SelectLabel className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
        Workspace Invites
      </SelectLabel>

      {workspaceInvites.map((invite) => (
        <div
          key={invite.id}
          className="mx-1 flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
        >
          <div className="flex items-center gap-2">
            <div className="flex h-5 w-5 items-center justify-center rounded bg-amber-100">
              <FolderOpen className="h-3 w-3 text-amber-600" />
            </div>
            <span className="text-sm font-medium text-gray-900">
              {invite.workspace.name}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Accept workspaceInvite={invite} />
            <Reject workspaceInvite={invite} />
          </div>
        </div>
      ))}
      <SelectSeparator className="my-2 bg-gray-100" />
    </SelectGroup>
  );
};

const Accept = ({ workspaceInvite }: { workspaceInvite: WorkspaceInvite }) => {
  const createWorkspaceMember = useCreateWorkspaceMember();
  const handleClick = () => {
    createWorkspaceMember.mutate({ workspace_invite_id: workspaceInvite.id });
  };
  return (
    <Button
      variant="ghost"
      size="tinyIcon"
      onClick={handleClick}
      isLoading={createWorkspaceMember.isLoading}
      className="h-6 w-6 text-green-600 hover:bg-green-100 hover:text-green-700"
    >
      <CheckCircle2 className="h-3 w-3" />
    </Button>
  );
};

const Reject = ({ workspaceInvite }: { workspaceInvite: WorkspaceInvite }) => {
  const deleteWorkspaceInvite = useDeleteWorkspaceInvite();
  const handleClick = () => {
    deleteWorkspaceInvite.mutate(workspaceInvite.id);
  };
  return (
    <Button
      variant="ghost"
      size="tinyIcon"
      onClick={handleClick}
      isLoading={deleteWorkspaceInvite.isLoading}
      className="h-6 w-6 text-red-600 hover:bg-red-100 hover:text-red-700"
    >
      <XCircle className="h-3 w-3" />
    </Button>
  );
};

const SelectWorkspace = () => {
  const workspaces = useActiveOrgWorkspaces();
  const activeWorkspace = useActiveWorkspace();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const handleSelect = (value: string) => {
    if (value === "create-workspace") {
      setOpen(true);
    } else {
      navigate(`/workspace/${value}/home`);
    }
  };
  return (
    <>
      <Select
        value={activeWorkspace?.id.toString()}
        onValueChange={handleSelect}
      >
        <SelectTrigger className="h-8 min-w-[200px] max-w-[200px] border-gray-200  shadow-sm transition-colors duration-150 hover:border-gray-300 hover:bg-gray-50">
          <div className="flex items-center gap-2 text-sm">
            {activeWorkspace ? (
              <>
                <SelectItemIcon variant="secondary">
                  <BriefcaseIcon className="h-3 w-3 text-white" />
                </SelectItemIcon>
                <OverflowDisplayTooltip
                  content={activeWorkspace.name}
                  side="top"
                  className="max-w-[96px] font-medium text-gray-900"
                >
                  {activeWorkspace.name}
                </OverflowDisplayTooltip>
              </>
            ) : (
              <>
                <FolderOpen className="h-4 w-4 text-gray-400" />
                <span className="text-gray-500">Select workspace</span>
              </>
            )}
          </div>
        </SelectTrigger>
        <SelectContent
          align="start"
          className="w-[256px] border-gray-200 shadow-lg"
        >
          <SelectGroup className="max-h-[450px] overflow-y-auto overflow-x-hidden">
            <SelectLabel className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              Workspaces
            </SelectLabel>
            {workspaces.map((workspace) => (
              <SelectItem
                key={workspace.id}
                value={workspace.id.toString()}
                className="cursor-pointer px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <SelectItemIcon variant="secondary">
                    <BriefcaseIcon className="h-3 w-3 text-white" />
                  </SelectItemIcon>
                  <OverflowDisplayTooltip
                    content={workspace.name}
                    side="top"
                    className="max-w-[156px] font-medium text-gray-900"
                  >
                    {workspace.name}
                  </OverflowDisplayTooltip>
                  {workspace.is_default && (
                    <span className="ml-auto rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                      Default
                    </span>
                  )}
                </div>
              </SelectItem>
            ))}
            <SelectSeparator className="my-1 bg-gray-100" />
          </SelectGroup>
          <WorkspaceInvites />
          <SelectItem
            value="create-workspace"
            className="cursor-pointer px-3 py-2 hover:bg-gray-50 focus:bg-gray-50"
          >
            <div className="flex items-center gap-3">
              <PlusSquare className="h-5 w-5 text-gray-400" />
              <span className="font-medium text-gray-700">
                Create workspace
              </span>
            </div>
          </SelectItem>
        </SelectContent>
      </Select>
      {open ? <WorkspaceModal setOpen={setOpen} /> : null}
    </>
  );
};

export default SelectWorkspace;
